import type { Playlist, Video } from 'api';
import cx from 'classnames';
import CardToolTip from 'components/card-tooltip';
import ShareModal from 'components/share-modal';
import ThreeDots from 'icons/ThreeDotsIcon';
import mixpanel from 'lib/analytics/mixpanel';
import type { SailthruFormattedItem } from 'lib/sailthru';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTooltipStore } from './store';

type PlaylistItem = Playlist & {
  isPlaylist: true;
};

interface AnimatedIconButtonProps {
  onClick?: () => void;
  callback?: (value: boolean) => void;
  talk: Video | PlaylistItem | SailthruFormattedItem | unknown;
  isDarkMode?: boolean;
  mixpanelTitle?: string;
  itemNumber?: string;
}

const AnimatedIconButton = ({
  callback,
  onClick,
  talk,
  isDarkMode = false,
  mixpanelTitle,
  itemNumber = ''
}: AnimatedIconButtonProps) => {
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);

  // Move share modal state here (outside tooltip)
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('share');
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const { onHideTooltip } = useTooltipStore();

  // Determine if this is a playlist or video
  const isPlaylist = useMemo(() => {
    return Boolean(
      talk &&
        typeof talk === 'object' &&
        'isPlaylist' in talk &&
        talk.isPlaylist
    );
  }, [talk]);

  // Type-safe access to item data
  const itemData = useMemo(() => {
    if (!talk || typeof talk !== 'object') return {};

    if (isPlaylist) {
      const playlist = talk as PlaylistItem;
      // Build canonical URL for playlists
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      return {
        id: playlist.id,
        slug: playlist.slug,
        title: playlist.title,
        canonicalUrl: `${origin}/playlists/${playlist.id}/${playlist.slug}`,
        videoContext: 'Playlist',
        presenterDisplayName: '', // Playlists don't have presenters
        videosCount: 'videos' in playlist ? playlist.videos?.totalCount || 0 : 0
      };
    } else {
      const video = talk as Partial<Video>;
      return {
        id: video.id,
        slug: video.slug,
        title: video.title,
        canonicalUrl: video.canonicalUrl,
        videoContext: video.videoContext || 'TED Talk',
        presenterDisplayName: video.presenterDisplayName || ''
      };
    }
  }, [talk, isPlaylist]);

  // Share modal handlers
  const handleShareModalClose = useCallback(() => {
    setShareModalOpen(false);
    setModalContent('share');
    setIframeLoaded(false);
  }, []);

  const handleEmbedClick = useCallback(() => {
    setModalContent('embed');
  }, []);

  const handleShareClick = useCallback(() => {
    setModalContent('share');
    setIframeLoaded(false);
  }, []);

  const handleShareButtonClick = useCallback(() => {
    // Close tooltip first
    setIsTooltipOpen(false);

    // Track the event
    if (mixpanelTitle) {
      mixpanel.track('homepage_click', {
        current_url: window.location.href,
        title: mixpanelTitle,
        item_number: '',
        link_type: 'share',
        value: ''
      });
    }

    // Open share modal
    setShareModalOpen(true);
  }, [mixpanelTitle]);

  // Generate share-related data
  const shareUrl = useMemo(() => {
    if (!itemData.canonicalUrl) return '';
    return `${itemData.canonicalUrl}?utm_campaign=tedspread&utm_medium=referral&utm_source=tedcomshare`;
  }, [itemData.canonicalUrl]);

  const shareMessage = useMemo(() => {
    if (isPlaylist) {
      const videosText = itemData.videosCount === 1 ? 'video' : 'videos';
      return `${itemData.title || ''} (${itemData.videosCount} ${videosText} | ${itemData.videoContext || ''})`;
    } else {
      return `${itemData.title || ''} (${itemData.presenterDisplayName || ''} | ${itemData.videoContext || ''})`;
    }
  }, [itemData, isPlaylist]);

  const embedToCopy = useMemo(() => {
    // Only videos can be embedded, not playlists
    if (isPlaylist || !itemData.slug || !itemData.title) return '';

    return [
      `<div style="max-width:1024px">`,
      '<div style="position:relative;height:0;padding-bottom:56.25%">',
      '<iframe ',
      `src="https://embed.ted.com/talks/${itemData.slug}" `,
      `width="1024px" `,
      `height="576px" `,
      `title="${itemData.title}" `,
      'style="position:absolute;left:0;top:0;width:100%;height:100%"  ',
      'frameborder="0" scrolling="no" ',
      'allowfullscreen>',
      '</iframe>',
      '</div>',
      '</div>'
    ].join('');
  }, [itemData.slug, itemData.title, isPlaylist]);

  const embedToDisplay = useMemo(() => {
    // Only videos can be embedded, not playlists
    if (isPlaylist || !itemData.slug || !itemData.title) return '';

    return [
      `<div style="max-width:1024px">`,
      '<div style="position:relative;height:0;padding-bottom:56.25%">',
      '<iframe ',
      `src="https://embed.ted.com/talks/${itemData.slug}?hideControls=true" `,
      `width="1024px" `,
      `height="576px" `,
      `title="${itemData.title}" `,
      'style="position:absolute;left:0;top:0;width:100%;height:100%"  ',
      'frameborder="0" scrolling="no" ',
      'allowfullscreen ',
      `onload="window.parent.postMessage('iframeLoaded', 'https://embed.ted.com')">`,
      '</iframe>',
      '</div>',
      '</div>'
    ].join('');
  }, [itemData.slug, itemData.title, isPlaylist]);

  const handleClick = () => {
    if (onClick) onClick();
    setIsClicked(true);
    setIsTooltipOpen(true);
    onHideTooltip();

    // Use a shorter timeout for the visual click effect to prevent interference
    setTimeout(() => setIsClicked(false), 200);

    if (mixpanelTitle)
      mixpanel.track('homepage_click', {
        current_url: window.location.href,
        title: mixpanelTitle,
        item_number: itemNumber,
        link_type: 'overflow',
        value: ''
      });
  };

  const onHide = useCallback(() => {
    setIsTooltipOpen(false);
    // Reset click state when tooltip is hidden to ensure clean state
    setIsClicked(false);
  }, []);

  useEffect(() => {
    if (callback) callback(isTooltipOpen);
  }, [isTooltipOpen, callback]);

  useEffect(() => {
    const updateOnResize = () => {
      onHide();
    };

    window.addEventListener('resize', updateOnResize);

    return () => {
      window.removeEventListener('resize', updateOnResize);
    };
  }, [onHide]);

  // Handle iframe loading for embed content
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data === 'iframeLoaded' &&
        event.origin === 'https://embed.ted.com'
      ) {
        setIframeLoaded(true);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <>
      <CardToolTip
        isOpen={isTooltipOpen}
        onHide={onHide}
        talk={itemData as Partial<Video>}
        isDarkMode={isDarkMode}
        mixpanelTitle={mixpanelTitle as string}
        onShareClick={handleShareButtonClick}
      >
        <div
          onClick={handleClick}
          className={cx(
            'relative cursor-pointer rounded-full transition-colors duration-200 ease-in-out',
            {
              'bg-gray-300 ring-2 ring-[#D0D0D0]': isClicked,
              'hover:bg-gray-100 bg-transparent': !isClicked
            }
          )}
        >
          <ThreeDots
            className={cx(
              'h-4 lg-tui:h-5',
              isDarkMode ? 'dark-svg-icon dark-svg-icon_fill' : ''
            )}
          />
        </div>
      </CardToolTip>

      {/* Render ShareModal outside the tooltip DOM tree */}
      <ShareModal
        modalOpen={shareModalOpen}
        handleModalClose={handleShareModalClose}
        handleShareClick={handleShareClick}
        handleEmbedClick={handleEmbedClick}
        embedToCopy={embedToCopy}
        embedToDisplay={embedToDisplay}
        iframeLoaded={iframeLoaded}
        shareUrl={shareUrl}
        shareMessage={shareMessage}
        nativeDownload=""
        audioDownload=""
        shortenedUrl={shareUrl}
        modalContent={modalContent}
        sendEvent={() => {}}
      />
    </>
  );
};

export default AnimatedIconButton;
